import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

// ── Types (mirrors PlatformController response shapes) ────────────────────

export type PlatformTenantStatus = "ONBOARDING" | "ACTIVE" | "SUSPENDED";

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  status: PlatformTenantStatus;
  logoPath: string;
  memberCount: number;
  runtimeLoaded: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformRawAlert {
  id: number;
  alertId: string;
  source: string;
  alertDate: string;
  title: string;
  category: string;
  locations: string[];
  receivedAt: string;
}

export interface RawAlertsPage {
  total: number;
  limit: number;
  offset: number;
  rows: PlatformRawAlert[];
}

export interface EngineStatus {
  tenants: { loaded: number; ids: string[] };
  sources: {
    orefHttp: {
      enabled: boolean;
      lastPollAt: string | null;
      lastPollAlertCount: number;
      inFlight: boolean;
      failures: Record<string, { consecutiveFailures: number; lastSuccessAt: string | null; lastError: string | null }>;
    };
    orefMqtt: {
      enabled: boolean;
      listeners: Array<{ index: number; connected: boolean; lastMessageAt: number | null; lastError: string }>;
      receivedCount: number;
      parsedCount: number;
      parseErrorCount: number;
      lastMessageAt: number | null;
    };
  };
}

// ── Query keys ───────────────────────────────────────────────────────────

export const PLATFORM_TENANTS_KEY = ["platform", "tenants"] as const;
export const PLATFORM_RAW_ALERTS_KEY = ["platform", "raw-alerts"] as const;
export const PLATFORM_ENGINE_STATUS_KEY = ["platform", "engine", "status"] as const;

// ── Fetchers ─────────────────────────────────────────────────────────────

export const fetchTenants = async (): Promise<PlatformTenant[]> => {
  const resp = await axios.get<PlatformTenant[], AxiosResponse<PlatformTenant[]>>("/platform/tenants");
  return resp.data;
};

export const fetchRawAlerts = async (params: { limit?: number; offset?: number; source?: string } = {}): Promise<RawAlertsPage> => {
  const resp = await axios.get<RawAlertsPage, AxiosResponse<RawAlertsPage>>("/platform/raw-alerts", { params });
  return resp.data;
};

export const fetchEngineStatus = async (): Promise<EngineStatus> => {
  const resp = await axios.get<EngineStatus, AxiosResponse<EngineStatus>>("/platform/engine/status");
  return resp.data;
};

export const activateTenant = async (id: string) =>
  (await axios.post<{ ok: boolean }, AxiosResponse<{ ok: boolean }>>(`/platform/tenants/${id}/activate`)).data;

export const suspendTenant = async (id: string) =>
  (await axios.post<{ ok: boolean }, AxiosResponse<{ ok: boolean }>>(`/platform/tenants/${id}/suspend`)).data;

// ── Hooks ────────────────────────────────────────────────────────────────

export const usePlatformTenants = () => {
  const { data, isPending: loading, error } = useQuery({
    queryKey: PLATFORM_TENANTS_KEY,
    queryFn: fetchTenants,
    refetchInterval: 15_000,
  });
  return { tenants: data ?? [], loading, error };
};

export const useRawAlerts = (params: { limit?: number; offset?: number; source?: string } = {}) => {
  const { data, isPending: loading, error, refetch } = useQuery({
    queryKey: [...PLATFORM_RAW_ALERTS_KEY, params],
    queryFn: () => fetchRawAlerts(params),
    refetchInterval: 10_000,
  });
  return { page: data, loading, error, refetch };
};

export const useEngineStatus = () => {
  const { data, isPending: loading, error } = useQuery({
    queryKey: PLATFORM_ENGINE_STATUS_KEY,
    queryFn: fetchEngineStatus,
    refetchInterval: 5_000,
  });
  return { status: data, loading, error };
};

export const useTenantMutations = () => {
  const qc = useQueryClient();
  const activate = useMutation({
    mutationFn: activateTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: PLATFORM_TENANTS_KEY }),
  });
  const suspend = useMutation({
    mutationFn: suspendTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: PLATFORM_TENANTS_KEY }),
  });
  return { activate, suspend };
};
